import { useTranslation } from "react-i18next";

import { Checkbox, Divider, FormControlLabel, Grid, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { AddressFields } from "../../../address-fields/AddressFields";
import { FormikTextField } from "../../../formik/FormikTextField";
import { FormikAutocompleteSelect } from "../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { ProceedingParticipantType } from "../../api/ClientFileApi";
import { ParticipantFormType, ParticipantMaxLength } from "./ParticipantForm.types";
import { participantTypeOptions } from "./ParticipantTypeLabels.utils";

interface Props<T extends ParticipantFormType> {
  formik: FormikProps<T>;
  showAlsoCreateGlobalCheckbox?: boolean;
  disabled?: boolean;
  /** The type is immutable after creation — edit dialogs render it read-only. */
  typeDisabled?: boolean;
  /** Restricts the offered types (e.g. client-specific creation from a consuming field). */
  allowedTypes?: ProceedingParticipantType[];
}

export function ParticipantFormFields<T extends ParticipantFormType>({
  formik,
  showAlsoCreateGlobalCheckbox = false,
  disabled = false,
  typeDisabled = false,
  allowedTypes,
}: Props<T>) {
  const { t } = useTranslation();
  // `createAsGlobal` only exists on the client-specific form shape; probe for it instead of
  // asserting the wider type, so a caller passing the plain shape can't silently render a
  // checkbox whose value nothing reads.
  const createAsGlobal = "createAsGlobal" in formik.values ? Boolean(formik.values.createAsGlobal) : false;

  return (
    <Stack pt={2} gap={3} divider={<Divider />}>
      {/* Master Data */}
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={6}>
          <FormikAutocompleteSelect
            formik={formik}
            fieldName="type"
            label={t("proceedingParticipants.create.type")}
            options={participantTypeOptions(t, allowedTypes)}
            required
            disabled={disabled || typeDisabled}
          />
        </Grid>
        <Grid size={12}>
          <FormikTextField
            formik={formik}
            fieldName="name"
            label={t("proceedingParticipants.create.name")}
            slotProps={{ input: { inputProps: { maxLength: ParticipantMaxLength.name } } }}
            required
            disabled={disabled}
          />
        </Grid>
      </Grid>

      {/* Address */}
      <Grid container rowSpacing={2} columnSpacing={2}>
        <AddressFields
          formik={formik}
          streetField="street"
          houseNumberField="houseNumber"
          zipCodeField="zipCode"
          cityField="city"
          countryField="country"
          disabled={disabled}
        />
      </Grid>

      {/* Note */}
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={12}>
          <FormikTextField
            formik={formik}
            fieldName="note"
            label={t("proceedingParticipants.create.note")}
            multiline
            rows={3}
            slotProps={{ input: { inputProps: { maxLength: ParticipantMaxLength.note } } }}
            disabled={disabled}
          />
        </Grid>
      </Grid>

      {showAlsoCreateGlobalCheckbox && (
        <Stack>
          <FormControlLabel
            control={
              <Checkbox
                name="createAsGlobal"
                checked={createAsGlobal}
                onChange={(_, checked) => void formik.setFieldValue("createAsGlobal", checked)}
                disabled={disabled}
              />
            }
            label={
              <Stack>
                <Typography variant="body2">{t("proceedingParticipants.create.alsoCreateGlobal")}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("proceedingParticipants.create.alsoCreateGlobalHelper")}
                </Typography>
              </Stack>
            }
          />
        </Stack>
      )}
    </Stack>
  );
}
